import {
    Box,
    Button,
    DataField,
    Popup,
    TimeElapsed,
    formatDateTime,
} from '@missionlabs/smartagent-app-components'
import useHasFeature, { AppFeatures, AppSubFeatures } from 'hooks/useHasFeature'
import CancelIcon from 'images/icon-cross.svg'
import EditIcon from 'images/icon-edit.svg'
import SaveIcon from 'images/tick.svg'
import binIcon from 'images/bin.svg'
import React, { useEffect, useRef, useState } from 'react'
import { H } from 'react-accessible-headings'
import { useDispatch, useSelector } from 'react-redux'
import { BeatLoader } from 'react-spinners'
import * as ContactAPI from 'services/api/api.contact'
import { updateContactAttributes } from 'store/contact/contact.actions'
import { CTR } from 'store/contact/contact.state'
import { createNotification } from 'store/notification/notification.actions'
import RootState from 'store/state'
import { formatPhoneNumber, formatStartOfIntNumbers, getContactDurationSeconds } from 'utils'
import { ChannelType } from 'views/Metrics/HistoricMetrics/types'

import './contact-details.scss'
import { selectAppId, selectInstanceId } from 'store/app/app.selectors'
import { setSelectedInteraction } from 'store/contactSearch/contactSearch.actions'

interface ContactDetailsCTR extends CTR {
    channelType: ChannelType
}
export interface Props {
    contact?: ContactDetailsCTR
    whitelist?: string[]
    widgetTitle?: string
    showDeleteContactButton?: boolean
}

function chunkArray(arr: Array<[string, string]>, whitelist?: string[]) {
    const chunks = []
    let i = 0

    arr = whitelist ? arr.filter(([key]) => whitelist.includes(key)) : arr

    while (i < arr.length) {
        chunks.push(arr.slice(i, (i += 4)))
    }

    return chunks
}

export const POLLING_INTERVAL_MS = 10000

export const ContactDetails: React.FC<Props> = (props) => {
    const contact = useSelector((state: RootState) => props.contact ?? state.contact)
    const callbacksEnabled = useSelector((state: RootState) => state.app.callbacksEnabled)
    const companyID = useSelector(selectAppId)
    const instanceID = useSelector(selectInstanceId)
    const dispatch = useDispatch()
    const [externalID, setExternalID] = useState<string>('')
    const [initialExternalID, setInitialExternalID] = useState<string>('')
    const [editable, setEditable] = useState<boolean>(false)
    const [showDeletePopup, setShowDeletePopup] = useState<boolean>(false)
    const [deleteInProgress, setDeleteInProgress] = useState<boolean>(false)
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)


    const inputRef = useRef<HTMLInputElement>(null)

    const hasAttributes = () => {
        if (contact?.attributes && Object.keys(contact.attributes).length > 0) return true
        return false
    }
    const hasFeature = useHasFeature()

    const canEditCall =
        contact?.channel === 'VOICE' &&
        hasFeature(AppFeatures.CALL_DETAILS, AppSubFeatures.CALL_DETAILS_EDIT)
    const canEditTask =
        contact?.channel === 'TASK' &&
        hasFeature(AppFeatures.TASK_DETAILS, AppSubFeatures.TASK_DETAILS_EDIT)
    const canEditChat =
        contact?.channel === 'CHAT' &&
        hasFeature(AppFeatures.CHAT_DETAILS, AppSubFeatures.CHAT_DETAILS_EDIT)
    const canEditAttributes = canEditCall || canEditTask || canEditChat

    const canViewPredictedAttributes = hasFeature(
        AppFeatures.AI_FEATURES,
        AppSubFeatures.AI_ACTION_FLOWS_SHOW_ACW_PREDICTION,
    )
    const canViewChatPreContactSummary = hasFeature(
        AppFeatures.AI_FEATURES,
        AppSubFeatures.AI_ACTION_FLOWS_PRE_CONTACT_SUMMARIES_CHAT,
    )
    const canViewEmailPreContactSummary = hasFeature(
        AppFeatures.AI_FEATURES,
        AppSubFeatures.AI_ACTION_FLOWS_PRE_CONTACT_SUMMARIES_EMAIL,
    )

    const shouldSkipRenderingAttribute = (key: string) => {
        if (
            (key.endsWith('-predicted') && !canViewPredictedAttributes) ||
            (key.startsWith('sa-chat-pre-contact-summary') && !canViewChatPreContactSummary) ||
            (key.startsWith('sa-email-pre-contact-summary') && !canViewEmailPreContactSummary)
        ) {
            return true
        }

        return false
    }

    const renderMore = () => {
        if (!contact?.attributes) return null

        const entries = Object.entries(contact.attributes)

        return chunkArray(entries, props.whitelist).map((chunk, i) => (
            <div key={i} className="row">
                {chunk.map(([key, value]) => {
                    if (shouldSkipRenderingAttribute(key)) return null

                    return (
                        <DataField key={key + i} title={key.replace('sa-', '')} highlightContent>
                            {value}
                        </DataField>
                    )
                })}
            </div>
        ))
    }

    const onCancel = () => {
        setEditable(false)
        setExternalID(initialExternalID)
    }

    const onSave = (contact: CTR) => {
        dispatch(updateContactAttributes(contact.ID, { externalID }, true))
        setEditable(false)
        setInitialExternalID(externalID)
    }

    const onEdit = () => {
        setEditable(true)
    }

    const onDelete = () => {
        setShowDeletePopup(true)
    }

    const onCloseDeletePopup = () => {
        setShowDeletePopup(false)
    }

    const onConfirmDelete = async () => {
        if (!contact?.ID || !companyID || !instanceID) {
            dispatch(createNotification({
                type: 'error',
                header: 'Delete Failed',
                text: 'Unable to delete contact. Missing required information.',
                persist: true
            }))
            return
        }

        setDeleteInProgress(true)

        try {
            await ContactAPI.startDeleteContact(companyID, instanceID, contact.ID)

            pollingIntervalRef.current = setInterval(async () => {
                try {
                    const response = await ContactAPI.getDeletionStatus(companyID, instanceID, contact.ID)

                    if (response.data.data.status === 'SUCCEEDED') {
                        if (pollingIntervalRef.current) {
                            clearInterval(pollingIntervalRef.current)
                            pollingIntervalRef.current = null
                        }

                        setShowDeletePopup(false)
                        setDeleteInProgress(false)

                        dispatch(createNotification({
                            type: 'success',
                            header: 'Contact Deleted',
                            text: 'The contact and all associated data have been successfully deleted.',
                            persist: true
                        }))

                        dispatch(setSelectedInteraction(undefined))

                    } else if (response.data.data.status === 'FAILED') {
                        if (pollingIntervalRef.current) {
                            clearInterval(pollingIntervalRef.current)
                            pollingIntervalRef.current = null
                        }

                        setShowDeletePopup(false)
                        setDeleteInProgress(false)

                        dispatch(createNotification({
                            type: 'error',
                            header: 'Delete Failed',
                            text: 'Failed to delete contact. Please try again.',
                            persist: true
                        }))
                    }
                } catch (error: any) {
                    if (error?.response?.status !== 404) {
                        if (pollingIntervalRef.current) {
                            clearInterval(pollingIntervalRef.current)
                            pollingIntervalRef.current = null
                        }

                        setShowDeletePopup(false)
                        setDeleteInProgress(false)

                        dispatch(createNotification({
                            type: 'error',
                            header: 'Delete Failed',
                            text: 'An error occurred while deleting the contact. Please try again.',
                            persist: true
                        }))
                    }
                }
            }, POLLING_INTERVAL_MS)
        } catch (error) {
            dispatch(createNotification({
                type: 'error',
                header: 'Delete Failed',
                text: 'Failed to start contact deletion. Please try again.',
                persist: true
            }))
            setShowDeletePopup(false)
            setDeleteInProgress(false)
        }
    }

    const timeOfCall = contact?.connectedToAgentTimestamp || contact?.initiationTimestamp

    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current)
            }
        }
    }, [])

    useEffect(() => {
        if (editable) {
            inputRef.current?.focus()
        }
    }, [editable])

    useEffect(() => {
        const externalID = contact?.attributes['sa-externalID'] ?? 'Unknown'
        setInitialExternalID(externalID)
        setExternalID(externalID)
    }, [contact?.ID, contact?.attributes?.['sa-externalID']])

    const headerTitle = props.widgetTitle
        ? props.widgetTitle
        : contact?.channel === 'TASK'
            ? 'Task Details'
            : 'Contact Details'

    const timeInQueue = contact?.inQueueDuration ?? 0

    const hasDeleteButtonPermission = hasFeature(AppFeatures.DELETE_CONTACT)

    return (
        <div className="contact-details-container">
            <Box
                className="contact-details"
                alt
                collapse
                boxLabel="Contact details"
                header={
                    <>
                        <H>{headerTitle}</H>
                        {props.showDeleteContactButton && hasDeleteButtonPermission && (
                            <Button
                                styling="delete"
                                onClick={onDelete}
                                icon={
                                    <img
                                        alt="delete bin"
                                        src={binIcon}
                                        style={{ marginRight: '4px', filter: 'brightness(0) invert(1)' }}
                                    />
                                }
                            >
                                Delete Contact
                            </Button>
                        )}
                    </>
                }
                more={
                    (!props.whitelist || props.whitelist.length) && hasAttributes()
                        ? renderMore()
                        : undefined
                }
            >
                {contact ? (
                    <>
                        <div className="row">
                            <DataField basis={50} title="Contact ID" highlightContent>
                                {contact.ID}
                            </DataField>
                            <DataField basis={25} title="Initiation Method" highlightContent>
                                {contact.initiationMethod}
                            </DataField>
                            <DataField basis={25} title="Duration" highlightContent>
                                <TimeElapsed
                                    ariaLive="off"
                                    date={TimeElapsed.from(getContactDurationSeconds(contact))}
                                    displayOnZero="unknown"
                                    annotate
                                    noTick
                                />
                            </DataField>
                        </div>
                        <div className="row quarters">
                            <DataField title="Start time" highlightContent>
                                {timeOfCall
                                    ? formatDateTime(timeOfCall, { date: true, time: true })
                                    : 'Unknown'}
                            </DataField>
                            <DataField title="External ID" highlightContent>
                                <div className={'data-field_editable row between'}>
                                    {editable ? (
                                        <>
                                            <input
                                                aria-label="Input external ID"
                                                ref={inputRef}
                                                value={externalID}
                                                onChange={(e) => setExternalID(e.target.value)}
                                                // Prevent event bubbling to DataField or else it deselects input on click
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                }}
                                            />
                                            <div className="data-field_editable_buttons">
                                                <Button
                                                    onClick={() => onSave(contact)}
                                                    styling="blank"
                                                    className="data-field_editable_button"
                                                    aria-label="External ID save button"
                                                >
                                                    <img
                                                        width={20}
                                                        height={20}
                                                        src={SaveIcon}
                                                        alt="Save"
                                                        title="Save"
                                                    />
                                                </Button>
                                                <Button
                                                    onClick={onCancel}
                                                    styling="blank"
                                                    className="data-field_editable_button"
                                                    aria-label="External ID cancel button"
                                                >
                                                    <img
                                                        width={20}
                                                        height={20}
                                                        src={CancelIcon}
                                                        alt="Cancel"
                                                        title="Cancel"
                                                    />
                                                </Button>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div>{externalID}</div>
                                            {canEditAttributes && (
                                                <Button
                                                    onClick={onEdit}
                                                    styling="blank"
                                                    aria-label="External ID edit button"
                                                >
                                                    <img
                                                        width={15}
                                                        height={15}
                                                        src={EditIcon}
                                                        alt="Edit"
                                                        title="Edit"
                                                    />
                                                </Button>
                                            )}
                                        </>
                                    )}
                                </div>
                            </DataField>

                            {contact.channel === 'TASK' ? (
                                <>
                                    <DataField title="Task" highlightContent>
                                        {contact.description ?? 'Unknown'}
                                    </DataField>

                                    <DataField title="‎‎‏‏‎ ‎" highlightContent>
                                        {' '}
                                    </DataField>
                                </>
                            ) : (
                                <>
                                    <DataField title="Customer Number" highlightContent>
                                        {[
                                            'WHATSAPP WHATSAPP-DM',
                                            'SMS SMS-DM',
                                            'SMS CAMPAIGN-MESSAGE',
                                            'SMS MARKETING-MESSAGE',
                                        ].includes((contact as ContactDetailsCTR).channelType)
                                            ? contact.attributes['sa-customer-endpoint-address']
                                            : formatStartOfIntNumbers(
                                                formatPhoneNumber(
                                                    contact.attributes['sa-dialled-number'] ??
                                                    contact.customerEndpointAddress,
                                                ),
                                            )}
                                    </DataField>

                                    <DataField title="System Number" highlightContent>
                                        {formatPhoneNumber(contact.systemEndpointAddress)}
                                    </DataField>
                                </>
                            )}
                        </div>
                        <div className="row quarters">
                            <DataField title="Queue" highlightContent>
                                {contact.queueName}
                            </DataField>

                            <DataField title="Reason for contact" highlightContent>
                                {contact.attributes['sa-reason'] ?? 'Unknown'}
                            </DataField>

                            <DataField title="Time in Queue" highlightContent>
                                {isNaN(timeInQueue) ? (
                                    'Unknown'
                                ) : (
                                    <TimeElapsed
                                        ariaLive="off"
                                        date={TimeElapsed.from(timeInQueue)}
                                        displayOnZero="unknown"
                                        annotate
                                        noTick
                                    />
                                )}
                            </DataField>

                            <DataField title="ACW Duration" highlightContent>
                                <TimeElapsed
                                    ariaLive="off"
                                    date={TimeElapsed.from(contact.afterContactWorkDuration ?? 0)}
                                    displayOnZero="unknown"
                                    annotate
                                    noTick
                                />
                            </DataField>
                        </div>
                        <div className="row quarters">
                            <DataField title="Agent" highlightContent>
                                {contact.agentName || 'Unknown'}
                            </DataField>

                            <DataField title="Disconnect reason" highlightContent>
                                {contact.disconnectReason ?? 'Unknown'}
                            </DataField>

                            {callbacksEnabled ? (
                                <>
                                    <DataField title="Callback" highlightContent>
                                        {contact.attributes?.callSource === 'outbound_callback_call'
                                            ? 'yes'
                                            : 'no'}
                                    </DataField>

                                    <DataField title="Attempt reached" highlightContent>
                                        {contact.attributes?.attemptsCount ?? 'Unknown'}
                                    </DataField>
                                </>
                            ) : null}
                        </div>
                        <div className="row">
                            <DataField title="Notes" highlightContent>
                                <div>
                                    {contact.attributes['sa-acw-notes'] ??
                                        contact.attributes['sa-notes'] ??
                                        'No notes'}
                                </div>
                            </DataField>
                        </div>
                    </>
                ) : (
                    <div data-testid="contact-details-loading" className="contact-details-loading">
                        <BeatLoader />
                    </div>
                )}
            </Box>
            {showDeletePopup && (
                <Popup center small title="Delete Contact" onClose={onCloseDeletePopup} close>
                    <div className="contact-details-delete-popup">
                        {deleteInProgress ? (
                            <>
                                <h3 className="contact-details-delete-popup-title">Deleting Contact...</h3>
                                <div className="contact-details-delete-popup-message">
                                    <div className="spinner" />
                                    <p>Please wait while we delete this contact and all associated data.</p>
                                </div>
                            </>
                        ) : (
                            <>
                                <h3 className="contact-details-delete-popup-title">Delete Contact?</h3>
                                <p className="contact-details-delete-popup-message">
                                    Are you sure you want to delete this contact and all associated
                                    information including, where relevant: recordings, transcripts, CTR,
                                    email content, and all associated metadata? This action cannot be
                                    undone.
                                </p>
                                <div className="contact-details-delete-popup-actions">
                                    <Button styling="simple" onClick={onCloseDeletePopup}>
                                        Cancel
                                    </Button>
                                    <Button styling="red" onClick={onConfirmDelete}>
                                        Confirm
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                </Popup>
            )}
        </div>
    )
}
